import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Buildings, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/**
 * Landing page for /invite/:token — public (no auth required).
 * Two modes:
 *  - Existing user (matched by email) → password confirmation redirects to login
 *  - New user → sign up form (name + password) creates account and auto-logs in
 */
export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/org/invitations/preview/${token}`);
        setPreview(data);
        if (data.name) setName(data.name);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invitation invalide ou expirée");
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!preview) return;
    if (!preview.user_already_exists && (!password || password.length < 8)) {
      return toast.error("Mot de passe requis (8 caractères minimum)");
    }
    setBusy(true);
    try {
      await api.post("/org/invitations/accept", {
        token,
        password: preview.user_already_exists ? null : password,
        name: preview.user_already_exists ? null : (name || null),
        specialty: preview.user_already_exists ? null : (specialty || null),
      });
      toast.success("Invitation acceptée");
      // Existing users need to log in normally; new users are auto-authenticated
      // by the backend setting the JWT cookie. Both flows land on /dashboard.
      setTimeout(() => nav(preview.user_already_exists ? "/login?invited=1" : "/dashboard"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'accepter l'invitation");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#F4F7FB] grid place-items-center px-6">
        <div className="max-w-md bg-white border border-[#E1E9F0] rounded-2xl p-10 text-center" data-testid="invite-error">
          <WarningCircle size={42} weight="duotone" className="text-[#D45A3F] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1E3A5F] mb-3">{error}</h1>
          <p className="text-[#5F758F] text-sm mb-6">Contactez l'établissement pour recevoir un nouveau lien.</p>
          <Button variant="outline" className="rounded-[10px]" onClick={() => nav("/")}>Retour à l'accueil</Button>
        </div>
      </div>
    );
  }
  if (!preview) return <div className="min-h-screen bg-[#F4F7FB] grid place-items-center text-[#5F758F]">Chargement…</div>;

  const roleLabel = { admin: "praticien", assistant: "assistant", research: "assistant de recherche clinique" }[preview.role] || preview.role;

  return (
    <div className="min-h-screen bg-[#F4F7FB] py-16 px-6">
      <div className="max-w-lg mx-auto bg-white border border-[#E1E9F0] rounded-2xl p-10" data-testid="invite-accept-card">
        <div className="w-14 h-14 rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center mb-5">
          <Buildings size={26} weight="duotone" className="text-[#3A8FA5]" />
        </div>
        <p className="text-xs tracking-[0.2em] uppercase text-[#3A8FA5] mb-3">Invitation</p>
        <h1 className="text-3xl font-bold text-[#1E3A5F] tracking-tight leading-tight mb-3">
          Rejoignez <span className="text-[#3A8FA5]">{preview.organization_name}</span> sur Questiomed
        </h1>
        <p className="text-[#5F758F] leading-relaxed mb-8">
          {preview.invited_by_name || "L'établissement"} vous invite en tant que <strong className="text-[#1E3A5F]">{roleLabel}</strong> à l'adresse <strong className="text-[#1E3A5F]">{preview.email}</strong>.
        </p>

        {preview.user_already_exists ? (
          <div className="bg-[#F4F7FB] border border-[#E1E9F0] rounded-[10px] px-4 py-4 mb-6">
            <p className="text-sm text-[#1E3A5F]"><CheckCircle size={16} weight="fill" className="inline text-[#3A8FA5] mr-1.5 align-text-bottom" /> Un compte Questiomed existe déjà pour cet email.</p>
            <p className="text-[13px] text-[#5F758F] mt-1.5">Cliquez pour rejoindre l'établissement puis connectez-vous normalement.</p>
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Votre nom</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr Prénom Nom" className="mt-2 rounded-[10px]" data-testid="invite-accept-name" />
            </div>
            {preview.role === "admin" && (
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Spécialité</Label>
                <Input value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="Cardiologie, Médecine générale…" className="mt-2 rounded-[10px]" data-testid="invite-accept-specialty" />
              </div>
            )}
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Mot de passe (8+ caractères)</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-[10px]" data-testid="invite-accept-password" />
            </div>
          </div>
        )}

        <Button className="w-full rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold py-6" onClick={submit} disabled={busy} data-testid="invite-accept-submit">
          {busy ? "…" : preview.user_already_exists ? "Rejoindre l'établissement" : "Créer mon compte & rejoindre"}
        </Button>
      </div>
    </div>
  );
}
